import { type FC, useCallback, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import qs from 'qs';

import {
  type AssetsAssetSchema,
  type AssetsAssetSchemaTypedWithId,
  httpPatchAssetsByAssetId,
} from '@iconik-io/iconik-api/src/services/assets';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import type { TimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { rangeToSmpteString } from '@iconik-io/shared-lib/src/utils/range_to_smpte_string';
import { Icon } from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import CopyButton from '../../components/copy_button/copy_button';
import { Link } from '../../components/link/link';
import { SegmentHeader } from '../../components/segment/segment_header';
import {
  FAKE_SUBCLIP_SEGMENT_ID,
  TIME_BASED_METADATA_KEY,
} from '../../constants/data';
import { SUBCLIP_DELETE_MODAL } from '../../constants/modals';
import { openModal } from '../../ducks/modals_manager/slice';
import { openPanel } from '../../ducks/panels/slice';
import { highlightSegmentsTemporarily } from '../../ducks/segments/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import TimecodeFieldWrapper from '../../ui/forms/field_wrappers/timecode/timecode';
import Form from '../../ui/forms/form';
import IconButton from '../../ui/icon_button/icon_button';

import styles from './asset_subclips.module.css';

type Props = {
  subclip: SearchItemEntity | AssetsAssetSchemaTypedWithId;
  timecodeOptions: TimecodeOptions | undefined;
  baseUrl: string;
};

const SubclipItem: FC<Props> = ({ subclip, timecodeOptions, baseUrl }) => {
  const dispatch = useAppDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTimecodeError, setShowTimecodeError] = useState(false);
  const playerContext = useBasePlayerContext();

  const { id: assetId } = useParams({ strict: false });

  const subclipUrl = `/asset/${subclip.id}`;
  const subclipItemAbsoluteUrl = `${baseUrl}/asset/${
    subclip.original_asset_id
  }?${qs.stringify({
    subclip_id: subclip.id,
    segment_id: subclip.original_segment_id,
  })}`;

  const subclipSegmentBasedOnAsset: SegmentEntityWithIdNormalized = {
    segment_type: 'GENERIC',
    id: subclip.id,
    asset_id: assetId ?? subclip.original_asset_id ?? '',
    date_created: subclip.date_created ?? undefined,
    time_start_milliseconds: subclip.time_start_milliseconds,
    time_end_milliseconds: subclip.time_end_milliseconds,
    user_id: subclip.created_by_user ?? undefined,
  };

  const handleSubmit = (data: Partial<AssetsAssetSchema>) => {
    setIsLoading(true);
    dispatch(httpPatchAssetsByAssetId(subclip.id, data))
      .then((response) => {
        const { time_end_milliseconds, time_start_milliseconds, title } =
          response.body;
        dispatch(
          updateEntities({
            search_items: {
              [subclip.id]: {
                title,
                time_end_milliseconds,
                time_start_milliseconds,
              },
            },
          }),
        );
        setIsEditMode(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTimecodeClick = useCallback(() => {
    playerContext.store.segment.setSelectedSegmentIds([subclip.id]);
  }, [subclip.id]);

  const handlePlayClick = useCallback(() => {
    playerContext.store.segment.setSelectedSegmentIds([subclip.id]);
    playerContext.store.playableMedia.play();
  }, [subclip.id]);

  const openMetadataPanel = (segmentId?: string) => {
    if (!segmentId) {
      return;
    }
    dispatch(openPanel(TIME_BASED_METADATA_KEY));
    dispatch(highlightSegmentsTemporarily([segmentId]));
  };

  if (subclip.status === 'DELETED') {
    return (
      <div className={styles.deleted} role="listitem">
        Subclip deleted
      </div>
    );
  }

  if (!subclip.date_created || !subclip.created_by_user) {
    return null;
  }

  return (
    <>
      <SegmentHeader
        segment={subclipSegmentBasedOnAsset}
        onEdit={() => {
          setIsEditMode(true);
        }}
        canEdit={!isEditMode}
      />
      {isEditMode ? (
        <Form<Partial<AssetsAssetSchema>>
          vertical
          onValidSubmit={(data) => {
            const { time_end_milliseconds, time_start_milliseconds } = data;
            const isInvalidTimecode =
              time_end_milliseconds &&
              time_start_milliseconds &&
              (time_start_milliseconds > time_end_milliseconds ||
                time_end_milliseconds < time_start_milliseconds);

            if (isInvalidTimecode) {
              setShowTimecodeError(true);
              return;
            }
            handleSubmit(data);
          }}
        >
          <TextFieldWrapper
            name="title"
            label="Title"
            value={subclip.title}
            validations="required"
            validationMessages={{ required: 'Title can not be empty' }}
          />
          {timecodeOptions ? (
            <div className={styles.time_range}>
              <TimecodeFieldWrapper
                name="time_start_milliseconds"
                timecodeOptions={timecodeOptions}
                value={subclip.time_start_milliseconds ?? undefined}
                className={styles.timecode_input}
                aria={{
                  label: 'Start timecode',
                }}
              />
              <span className={styles.timecode_separator}>-</span>
              <TimecodeFieldWrapper
                name="time_end_milliseconds"
                className={styles.timecode_input}
                timecodeOptions={timecodeOptions}
                value={
                  subclip.time_end_milliseconds ??
                  subclip.time_start_milliseconds ??
                  undefined
                }
                aria={{
                  label: 'End timecode',
                }}
              />
            </div>
          ) : null}
          {showTimecodeError && (
            <span className={styles.error}>Timecode is invalid</span>
          )}
          <div className={styles.controls}>
            <IconButton
              iconName="deletion_delete"
              onClick={() => {
                dispatch(
                  openModal({
                    id: SUBCLIP_DELETE_MODAL,
                    contextItems: [subclip],
                  }),
                );
              }}
              variant="danger"
              aria={{
                label: 'Delete subclip',
              }}
            />
            <div className={styles.controls_right}>
              <Button
                variant="danger"
                className={styles.edit_buttons}
                disabled={isLoading}
                onClick={() => {
                  setIsEditMode(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                disabled={isLoading}
                className={styles.edit_buttons}
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </Form>
      ) : (
        <>
          {subclip.title}

          <div className={styles.time_range}>
            {subclip.time_end_milliseconds !==
            subclip.time_start_milliseconds ? (
              <IconButton
                type="button"
                iconName="player_play"
                iconSizeUAD="2xs"
                className={styles.play_button}
                onClick={handlePlayClick}
                data-segment-handler={`${FAKE_SUBCLIP_SEGMENT_ID}${subclip.id}`}
                data-segment-start={subclip.time_start_milliseconds}
                data-segment-end={subclip.time_end_milliseconds}
                data-segment-play="true"
              />
            ) : null}
            {timecodeOptions ? (
              <button
                type="button"
                onClick={handleTimecodeClick}
                className={styles.timecode_button}
                data-segment-handler={`${FAKE_SUBCLIP_SEGMENT_ID}${subclip.id}`}
                data-segment-start={subclip.time_start_milliseconds}
                data-segment-end={subclip.time_end_milliseconds}
              >
                {rangeToSmpteString({
                  startMilliseconds: subclip.time_start_milliseconds,
                  endMilliseconds: subclip.time_end_milliseconds,
                  timecodeOptions,
                })}
              </button>
            ) : null}
          </div>
          <div className={styles.controls}>
            <IconButton
              iconName="Svg2022_uad_metadata"
              onClick={() => {
                openMetadataPanel(subclip.original_segment_id ?? undefined);
              }}
              aria={{
                label: 'Open metadata',
              }}
            />
            <div className={styles.controls_right}>
              <Link
                aria-label="Subclip Open link"
                className={styles.open_link_icon}
                to={subclipUrl}
              >
                <Icon name="open" />
              </Link>
              <CopyButton
                copy={subclipItemAbsoluteUrl}
                text="Copy link to this subclip"
                placement="top"
                iconName="link"
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SubclipItem;
